<template>
  <v-card outlined class="mb-4 openapi-import-panel">
    <!-- Toggle Header -->
    <v-card-title
      class="pa-3 d-flex align-center cursor-pointer"
      style="min-height: auto"
      @click="expanded = !expanded"
    >
      <v-icon small class="mr-2">mdi-file-import</v-icon>
      <span class="text-subtitle-2 font-weight-medium">Import from OpenAPI / Swagger</span>
      <v-chip x-small outlined class="ml-2">Optional</v-chip>
      <v-spacer />
      <v-icon small>{{ expanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
    </v-card-title>

    <!-- Expandable Content -->
    <v-expand-transition>
      <div v-if="expanded">
        <v-divider />
        <v-card-text class="pt-3 pb-2">
          <!-- Already loaded state -->
          <template v-if="specInfo">
            <v-alert type="success" dense text class="mb-3">
              <div class="d-flex align-center flex-wrap">
                <div class="flex-grow-1">
                  <strong>{{ specInfo.title }}</strong>
                  <span class="text--secondary ml-1">({{ specInfo.endpoints.length }} endpoints)</span>
                </div>
                <div class="mt-1 mt-sm-0">
                  <v-btn small text color="primary" @click="showEndpointDialog = true">
                    <v-icon small left>mdi-format-list-bulleted</v-icon>
                    Select Endpoint
                  </v-btn>
                  <v-btn small text @click="clearSpec">
                    <v-icon small left>mdi-close</v-icon>
                    Clear
                  </v-btn>
                </div>
              </div>
            </v-alert>
          </template>

          <!-- Input tabs -->
          <template v-else>
            <v-tabs v-model="activeTab" dense height="36" class="mb-3">
              <v-tab class="text-none text-caption">
                <v-icon x-small left>mdi-link-variant</v-icon> From URL
              </v-tab>
              <v-tab class="text-none text-caption">
                <v-icon x-small left>mdi-upload</v-icon> Upload File
              </v-tab>
            </v-tabs>

            <v-tabs-items v-model="activeTab">
              <!-- Tab: From URL -->
              <v-tab-item>
                <v-row dense align="center">
                  <v-col>
                    <v-text-field
                      v-model="specUrl"
                      label="OpenAPI / Swagger URL"
                      placeholder="https://api.example.com/openapi.json"
                      outlined
                      dense
                      hide-details="auto"
                      @keyup.enter="fetchSpec"
                    />
                  </v-col>
                  <v-col cols="auto">
                    <v-btn
                      outlined
                      color="primary"
                      :loading="loading"
                      :disabled="!specUrl"
                      @click="fetchSpec"
                    >
                      <v-icon small left>mdi-download</v-icon> Fetch
                    </v-btn>
                  </v-col>
                </v-row>
              </v-tab-item>

              <!-- Tab: Upload File -->
              <v-tab-item>
                <v-file-input
                  v-model="selectedFile"
                  label="Select OpenAPI / Swagger JSON file"
                  accept=".json,application/json"
                  outlined
                  dense
                  prepend-icon="mdi-code-json"
                  hide-details="auto"
                  @change="onFileSelected"
                />
              </v-tab-item>
            </v-tabs-items>
          </template>

          <!-- Error display -->
          <v-alert
            v-if="errorMessage"
            type="error"
            dense
            text
            dismissible
            class="mt-3 mb-0"
            @input="errorMessage = null"
          >
            {{ errorMessage }}
          </v-alert>
        </v-card-text>
      </div>
    </v-expand-transition>

    <!-- Endpoint Selection Dialog -->
    <v-dialog v-model="showEndpointDialog" max-width="720" scrollable>
      <v-card>
        <v-card-title class="py-3">
          <v-icon class="mr-2">mdi-format-list-bulleted</v-icon>
          Select an Endpoint
        </v-card-title>

        <v-card-subtitle v-if="specInfo" class="pb-0">
          {{ specInfo.title }}
        </v-card-subtitle>

        <v-card-text class="pt-3 pb-0">
          <v-text-field
            v-model="endpointSearch"
            placeholder="Filter endpoints..."
            prepend-inner-icon="mdi-magnify"
            outlined
            dense
            clearable
            hide-details
            class="mb-2"
          />

          <v-alert dense text type="info" class="mb-2 text-caption">
            Only GET and POST endpoints are shown.
          </v-alert>

          <v-list dense class="endpoint-list" style="max-height: 400px; overflow-y: auto">
            <v-list-item
              v-for="(ep, idx) in filteredEndpoints"
              :key="idx"
              @click="selectEndpoint(ep)"
              class="endpoint-item"
            >
              <v-list-item-action class="mr-3 my-0" style="min-width: auto">
                <v-chip
                  x-small
                  :color="ep.method === 'GET' ? 'green' : 'orange darken-1'"
                  dark
                  label
                  class="font-weight-bold"
                  style="min-width: 42px; justify-content: center"
                >
                  {{ ep.method }}
                </v-chip>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title class="text-body-2 code-font">
                  {{ ep.path }}
                  <v-chip
                    v-if="ep.deprecated"
                    x-small
                    color="warning"
                    outlined
                    class="ml-2"
                  >
                    deprecated
                  </v-chip>
                </v-list-item-title>
                <v-list-item-subtitle class="text-caption">
                  {{ ep.summary || ep.operationId || 'No description' }}
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action v-if="ep.tags.length" class="my-0 ml-2">
                <div class="d-flex flex-wrap justify-end" style="gap: 2px">
                  <v-chip
                    v-for="tag in ep.tags.slice(0, 2)"
                    :key="tag"
                    x-small
                    outlined
                  >
                    {{ tag }}
                  </v-chip>
                </div>
              </v-list-item-action>
            </v-list-item>

            <v-list-item v-if="filteredEndpoints.length === 0" disabled>
              <v-list-item-content>
                <v-list-item-title class="text-center text--secondary">
                  {{ endpointSearch ? 'No matching endpoints' : 'No GET/POST endpoints found' }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>

        <v-card-actions class="px-4 py-3">
          <span class="text-caption text--secondary">
            {{ filteredEndpoints.length }} endpoint{{ filteredEndpoints.length !== 1 ? 's' : '' }}
          </span>
          <v-spacer />
          <v-btn text @click="showEndpointDialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import axios from 'axios';
import { parseOpenApiSpec, extractConfigFromEndpoint } from '@/features/Registers/modules/openApiParser';

export default {
  name: 'SharedOpenApiImportPanel',
  data() {
    return {
      expanded: false,
      activeTab: 0,
      specUrl: '',
      selectedFile: null,
      loading: false,
      errorMessage: null,
      rawSpec: null,
      specInfo: null,
      showEndpointDialog: false,
      endpointSearch: '',
    };
  },
  computed: {
    filteredEndpoints() {
      if (!this.specInfo) return [];
      const endpoints = this.specInfo.endpoints;
      if (!this.endpointSearch) return endpoints;

      const q = this.endpointSearch.toLowerCase();
      return endpoints.filter(ep =>
        ep.path.toLowerCase().includes(q)
        || ep.summary.toLowerCase().includes(q)
        || ep.operationId.toLowerCase().includes(q)
        || ep.tags.some(t => t.toLowerCase().includes(q))
      );
    },
  },
  methods: {
    async fetchSpec() {
      if (!this.specUrl) return;
      this.loading = true;
      this.errorMessage = null;

      try {
        const { data } = await axios.get(this.specUrl, {
          timeout: 15000,
          headers: { Accept: 'application/json' },
        });
        const spec = typeof data === 'string' ? JSON.parse(data) : data;
        this.processSpec(spec);
      } catch (err) {
        if (err.message?.includes('Network Error') || err.code === 'ERR_NETWORK') {
          this.errorMessage =
            'Could not fetch the URL (likely blocked by CORS). Try downloading the file and uploading it instead.';
        } else if (err instanceof SyntaxError) {
          this.errorMessage =
            'The response is not valid JSON. Only JSON format is supported — if the spec is YAML, convert it to JSON first.';
        } else {
          this.errorMessage = err.response?.data?.message || err.message || 'Failed to fetch specification.';
        }
      } finally {
        this.loading = false;
      }
    },

    onFileSelected(file) {
      if (!file) return;
      this.errorMessage = null;

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const spec = JSON.parse(e.target.result);
          this.processSpec(spec);
        } catch {
          this.errorMessage =
            'Invalid JSON file. Only JSON format is supported — if the spec is YAML, convert it to JSON first.';
        }
      };
      reader.onerror = () => {
        this.errorMessage = 'Failed to read the file.';
      };
      reader.readAsText(file);
    },

    processSpec(spec) {
      try {
        const info = parseOpenApiSpec(spec);
        if (info.endpoints.length === 0) {
          this.errorMessage = 'No GET or POST endpoints found in this specification.';
          return;
        }
        this.rawSpec = spec;
        this.specInfo = info;
        this.showEndpointDialog = true;
      } catch (err) {
        this.errorMessage = err.message;
      }
    },

    selectEndpoint(endpoint) {
      const configPatch = extractConfigFromEndpoint(this.rawSpec, endpoint);
      this.$emit('apply-config', configPatch);
      this.showEndpointDialog = false;
      this.expanded = false;
    },

    clearSpec() {
      this.rawSpec = null;
      this.specInfo = null;
      this.specUrl = '';
      this.selectedFile = null;
      this.endpointSearch = '';
      this.errorMessage = null;
    },
  },
};
</script>

<style scoped>
.openapi-import-panel .cursor-pointer {
  cursor: pointer;
}

.openapi-import-panel .cursor-pointer:hover {
  background: rgba(0, 0, 0, 0.02);
}

.code-font {
  font-family: 'Roboto Mono', monospace;
  font-size: 13px;
}

.endpoint-item {
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.endpoint-item:hover {
  background: rgba(0, 0, 0, 0.03);
}

.endpoint-list {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
}
</style>
